import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { EvidenceBadge, ConfidentialBadge } from "@/components/common/EvidenceBadge";
import { HERO_ALT } from "@/lib/content";

const FILTERS = [
  { key: "technology_family", label: "Technology family", tax: "technology_families" },
  { key: "application_environment", label: "Application environment", tax: "application_environments" },
  { key: "project_stage", label: "Project stage", tax: "project_stages" },
  { key: "evidence_status", label: "Evidence status", tax: "evidence" },
  { key: "discipline", label: "Discipline", tax: "disciplines" },
  { key: "output_type", label: "Output type", tax: "output_types" },
];

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [tax, setTax] = useState({});
  const [projects, setProjects] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/taxonomy").then(({ data }) => setTax(data)); }, []);

  const filters = useMemo(() => {
    const o = {};
    FILTERS.forEach((f) => params.get(f.key) && (o[f.key] = params.get(f.key)));
    return o;
  }, [params]);

  useEffect(() => {
    setLoading(true);
    api.get("/projects", { params: filters }).then(({ data }) => {
      setProjects(data.results);
      setNote(data.confidentiality_note);
      setLoading(false);
    });
  }, [filters]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || next.get(k) === v) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };
  const chips = Object.entries(filters);

  return (
    <>
      <PageMeta title="Projects" description="DBRF's OEM scientific and engineering portfolio, described within confidentiality boundaries." />
      <PageHero
        eyebrow="OEM portfolio"
        title="Depth of work, within confidentiality."
        intro="DBRF has operated as an OEM scientific and engineering resource — contributing research, architecture, algorithms, software, prototypes, documentation, validation methods and IP development for third-party technology programmes."
        image="/assets/05-projects-hero.webp"
        alt={HERO_ALT.projects}
        objectPosition="center 40%"
      />

      <section className="container-dbrf py-12">
        <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6" data-testid="confidentiality-note">
          <p className="eyebrow mb-2">Confidentiality and ownership</p>
          <div className="text-sm text-[var(--mineral)] space-y-3" style={{ lineHeight: 1.7 }}>
            {note.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="container-dbrf pb-20">
        <div className="grid gap-10 lg:grid-cols-[280px_1fr]">
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="project-filters">
            {FILTERS.map((f) => (
              <div key={f.key} className="mb-5">
                <p className="eyebrow mb-2">{f.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(tax[f.tax] || []).map((opt) => {
                    const isActive = params.get(f.key) === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setFilter(f.key, opt)}
                        aria-pressed={isActive}
                        data-testid={`pfilter-${f.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`btn-transition rounded-full border px-2.5 py-1 text-xs ${isActive ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white" : "border-[var(--line)] bg-[var(--paper)] text-navy hover:border-[var(--cobalt)]"}`}
                      >
                        {isActive ? "✓ " : ""}{opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </aside>

          <div>
            <div className="mb-6 flex items-center gap-3">
              <span data-testid="project-count" className="font-mono text-sm text-[var(--mineral)]" aria-live="polite">
                {loading ? "Loading…" : `${projects.length} project${projects.length === 1 ? "" : "s"}`}
              </span>
              {chips.length > 0 && (
                <button onClick={() => setParams(new URLSearchParams(), { replace: true })} data-testid="project-clear-all" className="text-xs font-semibold text-[var(--cobalt)] link-underline">Clear all</button>
              )}
            </div>
            {chips.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-2">
                {chips.map(([k, v]) => (
                  <button key={k} onClick={() => setFilter(k, null)} data-testid={`pchip-${k}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--navy)] px-3 py-1 text-xs text-white">{v} <X size={12} /></button>
                ))}
              </div>
            )}

            {projects.length === 0 && !loading ? (
              <div className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--paper)] p-12 text-center">
                <p className="font-display text-lg text-navy">No projects match these filters.</p>
                <button onClick={() => setParams(new URLSearchParams(), { replace: true })} className="mt-2 text-sm font-semibold text-[var(--cobalt)]">Clear filters</button>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {projects.map((p) => (
                  <Link key={p.slug} to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="card-lift flex flex-col rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-7">
                    <div className="flex flex-wrap items-center gap-2">
                      <ConfidentialBadge />
                      <EvidenceBadge status={p.evidence_status} />
                    </div>
                    <span className="mt-4 font-mono text-[0.66rem] uppercase tracking-wide text-[var(--mineral)]">{p.project_stage}</span>
                    <h3 className="mt-1 font-display text-lg font-bold text-navy">{p.public_title}</h3>
                    <p className="mt-2 flex-1 text-sm text-[var(--mineral)]">{p.short_challenge}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(p.skills_required || []).slice(0, 5).map((s) => (
                        <span key={s} className="rounded-full border border-[var(--line)] bg-[var(--ivory)] px-2 py-0.5 font-mono text-[0.62rem] text-[var(--mineral)]">{s}</span>
                      ))}
                    </div>
                    <p className="mt-4 border-t border-[var(--line)] pt-3 font-mono text-[0.64rem] text-[var(--mineral)]">Outputs: {(p.output_types || []).slice(0, 3).join(", ")}</p>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--cobalt)]">View record <ArrowRight size={14} /></span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
